import { catmatTalidomida } from 'api/ImpressaoMedicamentos'
import { Checkbox, HFlow, Tag, Text, Tooltip, VFlow } from 'bold-ui'
import { useFlags } from 'config/useFlagsContext'
import { css } from 'emotion'
import { TipoReceitaEnum } from 'graphql/types.generated'
import { isUndefinedOrNull } from 'util/checks'
import { TipoReceitaTag } from 'view/atendimentos/detail/components/TipoReceitaTag'

import { MedicamentoFormModel } from '../../model-prescricao'
import { resolveMedicamento } from '../../utils/converter-prescricao'
import {
  checkIsAgonistaIncompativel,
  checkIsAntimicrobianoIncompativel,
  hasIsotretinoinaTretinoinaAcitretina,
  isOnPrescricaoDigitalAtiva,
} from '../../utils/utils-prescricao'
import {
  MedicamentoPrescricaoDigitalSelected,
  PrescricaoDigitalFormModel,
} from '../prescricao-digital/model-prescricaoDigital'
import { MedicamentoImpressoInfoLabel } from './MedicamentoImpressoInfoLabel'
import { MedicamentoOnPrescricaoDigitalInfoLabel } from './MedicamentoOnPrescricaoDigitalInfoLabel'
import { PrescricaoBoxItem } from './PrescricaoBoxItem'

interface MedicamentoItemProps {
  medicamento: MedicamentoFormModel
  prescricaoDigital: PrescricaoDigitalFormModel
  index: number
  isPrescricaoDigital: boolean
  disableReceitaControlada: boolean
  isPrinted(medicamentoId: ID): boolean
  onChange(checked: boolean, medicamento: MedicamentoFormModel): void
  checked(medicamentoId: ID): boolean
  medicamentoSelected?: MedicamentoPrescricaoDigitalSelected | null
  isReimpressao?: boolean
}

const getTooltipText = (
  isDisabledMedicamentoReceitaControlada: boolean,
  isMedicamentoAlreadyOnPrescricaoDigital: boolean,
  isDisabledByTipoReceitaSelecionado: boolean,
  isDisabledByCategoriaIncompativel: boolean,
  isDisabledByAntimicrobianoIncompativel: boolean,
  isDisabledByAgonistaIncompativel: boolean
): string | null => {
  if (isDisabledMedicamentoReceitaControlada) {
    return 'Não é possível incluir medicamentos de uso controlado na prescrição digital'
  }

  if (isMedicamentoAlreadyOnPrescricaoDigital) {
    return 'Medicamento já incluído em uma prescrição digital'
  }

  if (isDisabledByTipoReceitaSelecionado) {
    return 'Não é possível selecionar medicamentos com tipos de receita diferentes'
  }

  if (isDisabledByCategoriaIncompativel || isDisabledByAntimicrobianoIncompativel || isDisabledByAgonistaIncompativel) {
    return 'Medicamentos dessa categoria não podem ser incluídos na mesma prescrição que medicamentos de categorias de controle especial diferentes'
  }

  return null
}

export const MedicamentoItem = (props: MedicamentoItemProps) => {
  const {
    medicamento,
    prescricaoDigital,
    index,
    isPrescricaoDigital,
    disableReceitaControlada,
    isPrinted,
    isReimpressao,
    onChange,
    checked,
    medicamentoSelected,
  } = props

  const { principioAtivo, concentracao, tipoReceita, categoria } = resolveMedicamento(medicamento)

  const { PILOTO_PRESCRICAO_CONTROLADA_ENABLED } = useFlags()

  const isMedicamentoAlreadyOnPrescricaoDigital =
    isPrescricaoDigital && isOnPrescricaoDigitalAtiva(prescricaoDigital, medicamento)

  const isDisabledMedicamentoReceitaControlada =
    !PILOTO_PRESCRICAO_CONTROLADA_ENABLED && disableReceitaControlada && tipoReceita !== TipoReceitaEnum.COMUM

  const isDisabledByTipoReceitaSelecionado = !!medicamentoSelected && tipoReceita !== medicamentoSelected.tipoReceita

  const isDisabledByCategoriaIncompativel =
    !!categoria &&
    !isUndefinedOrNull(medicamentoSelected?.categoriasCompativeis) &&
    !medicamentoSelected?.categoriasCompativeis?.includes(categoria)

  const isDisabledByAntimicrobianoIncompativel = checkIsAntimicrobianoIncompativel(
    categoria,
    tipoReceita,
    medicamento.registroManual,
    medicamentoSelected
  )

  const isDisabledByAgonistaIncompativel = checkIsAgonistaIncompativel(categoria, tipoReceita, medicamentoSelected)

  const isMedicamentoDisabled =
    isDisabledMedicamentoReceitaControlada ||
    isMedicamentoAlreadyOnPrescricaoDigital ||
    isDisabledByTipoReceitaSelecionado ||
    isDisabledByCategoriaIncompativel ||
    isDisabledByAntimicrobianoIncompativel ||
    isDisabledByAgonistaIncompativel

  const isTalidomida = medicamento.principioAtivoCombo?.codigoCatMat === catmatTalidomida

  const hasTags = medicamento.usoContinuo || tipoReceita !== TipoReceitaEnum.COMUM

  const tooltipText = getTooltipText(
    isDisabledMedicamentoReceitaControlada,
    isMedicamentoAlreadyOnPrescricaoDigital,
    isDisabledByTipoReceitaSelecionado,
    isDisabledByCategoriaIncompativel,
    isDisabledByAntimicrobianoIncompativel,
    isDisabledByAgonistaIncompativel
  )

  return (
    <PrescricaoBoxItem key={medicamento.id} data-cy={`Medicamento${index}`}>
      <HFlow justifyContent='space-between'>
        <HFlow>
          <Tooltip text={tooltipText}>
            <span
              css={css`
                margin: auto;
              `}
            >
              <Checkbox
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked, medicamento)}
                onKeyDown={(e) => e.stopPropagation()}
                checked={!isMedicamentoDisabled && checked(medicamento.id)}
                disabled={isMedicamentoDisabled}
                data-cy={`Medicamento${index}Checkbox`}
              />
            </span>
          </Tooltip>

          <VFlow vSpacing={0.25}>
            <Text fontWeight='bold' data-cy={`PrincipioAtivo${index}`}>{`${principioAtivo} ${concentracao}`}</Text>
            {hasTags && (
              <HFlow>
                {medicamento.usoContinuo && (
                  <Tag
                    type='normal'
                    style={css`
                      align-self: center;
                      max-height: 1.5rem;
                    `}
                  >
                    Uso contínuo
                  </Tag>
                )}
                {tipoReceita !== TipoReceitaEnum.COMUM && (
                  <TipoReceitaTag
                    tipoReceita={tipoReceita}
                    style={css`
                      align-self: center;
                      max-height: 1.5rem;
                    `}
                  />
                )}
              </HFlow>
            )}
            {(isTalidomida || hasIsotretinoinaTretinoinaAcitretina([medicamento])) && (
              <Tag
                type='normal'
                style={css`
                  align-self: center;
                  max-height: 1.5rem;
                `}
              >
                Medicamento com termo de uso e consentimento
              </Tag>
            )}
            <Text data-cy={`Posologia${index}`}>{medicamento.posologia}</Text>
          </VFlow>
        </HFlow>
        <VFlow
          vSpacing={0.25}
          style={css`
            align-items: end;
            justify-content: center;
          `}
        >
          {isOnPrescricaoDigitalAtiva(prescricaoDigital, medicamento) && <MedicamentoOnPrescricaoDigitalInfoLabel />}
          {!isReimpressao && isPrinted(medicamento.id) && <MedicamentoImpressoInfoLabel />}
        </VFlow>
      </HFlow>
    </PrescricaoBoxItem>
  )
}
